import Link from '@components/ui/Link'
import { ReactComponent as SpokeLogoIcon } from '@svgs/logo/spoke-logo-with-border.svg'
import { ReactComponent as TickIcon } from '@svgs/chalk/tick.svg'
import { ReactComponent as ThumbsDownIcon } from '@svgs/chalk/thumbs-down.svg'
import { ReactComponent as CrossIcon } from '@svgs/chalk/cross.svg'
import styles from './styles.module.scss'
import { File } from 'types/contentful/graphQlApi'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'

type ImageCard = {
  title: string
  file: File
}

type BulletPointIconProps = 'icon-cross' | 'icon-thumbs-down'

export type ComparisonCardProps = {
  title: string
  showIcon: boolean | null
  imageDesktop: ImageCard
  imageMobile: ImageCard
  backgroundColor: string | null
  themeColor: 'dark-theme' | 'light-theme'
  isBrandedCard: boolean
  benefit1: string
  benefit2: string
  benefit3: string
  bulletPointIcon?: BulletPointIconProps
  button: {
    uri: string
    text: string
  }
}

function getCardIcon(
  isBrandedCard: boolean,
  bulletPointIcon?: BulletPointIconProps
) {
  if (isBrandedCard) return TickIcon
  switch (bulletPointIcon) {
    case 'icon-cross':
      return CrossIcon
    case 'icon-thumbs-down':
      return ThumbsDownIcon
    default:
      return TickIcon
  }
}

export default function ComparisonCard({
  title,
  imageDesktop,
  imageMobile,
  themeColor = 'dark-theme',
  button,
  benefit1,
  benefit2,
  benefit3,
  bulletPointIcon,
  isBrandedCard,
  showIcon
}: ComparisonCardProps) {
  const Icon = getCardIcon(isBrandedCard, bulletPointIcon)
  const isTabletUp = useMediaQuery({
    minWidth: BREAKPOINT_SIZES.TABLET,
    initialState: false
  })

  const benefits = [benefit1, benefit2, benefit3].filter(Boolean)

  return (
    <article className={`${styles.ComparisonCard} ${styles[themeColor]}`}>
      <div className={styles.ComparisonCard__image}>
        <picture>
          <source media="(min-width: 601px)" srcSet={imageDesktop.file.url} />
          <img alt={title} src={imageMobile.file.url} />
        </picture>
      </div>
      <div className={styles.ComparisonCard__content}>
        <div
          className={`${styles.ComparisonCard__title} ${
            !isBrandedCard ? styles.ComparisonCard__title_nonBranded : ''
          }`}
        >
          {showIcon ? (
            <SpokeLogoIcon
              width={62}
              height={51}
              aria-hidden="true"
              focusable="false"
            />
          ) : (
            <h3>{title}</h3>
          )}
        </div>

        <ul>
          {benefits.map((benefit, i) => (
            <li key={i}>
              <Icon
                width={24}
                height={24}
                aria-hidden="true"
                focusable="false"
              />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
        {button?.uri && (
          <Link
            variant="primary"
            classes={`${styles.ComparisonCard__contentButton} ${
              isTabletUp ? styles[themeColor] : ''
            }`}
            href={button.uri}
          >
            {button.text}
          </Link>
        )}
      </div>
    </article>
  )
}
